import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LOCAL_STORAGE } from "@constants/index";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants/index";
import { removeLocalStorage } from "@utils/local-storage";
import i18n from "i18next";

/**
 * Cross-tab auth: logout when TOKEN/USER_ID cleared; reload on user switch.
 * Access-token rotation (TOKEN value change) is handled in-place by axios — no reload.
 */
const useCrossTabAuth = (clearPermissions) => {
  const isHandling = useRef(false);
  const notificationShown = useRef(false);
  const navigate = useNavigate();

  const handleUserLogout = useCallback(() => {
    if (isHandling.current || notificationShown.current) return;

    isHandling.current = true;
    notificationShown.current = true;
    ToastConstant(
      TOAST.info,
      i18n.t("toast.login.session-ended.title"),
      i18n.t("toast.login.session-ended.message"),
    );
    removeLocalStorage([
      LOCAL_STORAGE.TOKEN,
      LOCAL_STORAGE.SESSION_ID,
      LOCAL_STORAGE.ACCESS_TOKEN,
      LOCAL_STORAGE.USER_ID,
    ]);
    localStorage.removeItem(LOCAL_STORAGE.PERMISSIONS);

    if (clearPermissions) {
      clearPermissions();
    }
    setTimeout(() => {
      navigate("/login", { replace: true });
    }, 2000);
  }, [navigate, clearPermissions]);

  const handleUserChange = useCallback(() => {
    if (isHandling.current || notificationShown.current) return;

    isHandling.current = true;
    notificationShown.current = true;

    ToastConstant(
      TOAST.info,
      i18n.t("toast.login.session-updated.title"),
      i18n.t("toast.login.session-updated.message"),
    );
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.storageArea !== localStorage || e.key === null) {
        return;
      }

      if (e.key === LOCAL_STORAGE.TOKEN) {
        if (e.oldValue !== null && e.newValue === null) {
          handleUserLogout();
        }
        return;
      }

      if (e.key === LOCAL_STORAGE.USER_ID) {
        const newUserId = e.newValue;
        const oldUserId = e.oldValue;

        if (oldUserId !== null && newUserId === null) {
          handleUserLogout();
          return;
        }

        if (newUserId !== oldUserId && newUserId !== null && oldUserId !== null) {
          handleUserChange();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleUserLogout, handleUserChange]);
};

export default useCrossTabAuth;
